import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FailoverRow {
  readonly failoverId: string;
  readonly failoverCode: string;
  readonly affectedService: string;
  readonly failoverType: string;
  readonly sourceEnvironment: string | null;
  readonly targetEnvironment: string | null;
  readonly status: string;
  readonly downtimeSeconds: number | null;
}

const TYPES = ['', 'AUTOMATIC', 'MANUAL', 'PLANNED', 'EMERGENCY', 'PARTIAL', 'FULL'];
const STATUSES = ['', 'INITIATED', 'IN_PROGRESS', 'COMPLETED', 'FAILED'];
const INITIATE_TYPES = TYPES.filter(Boolean);

/**
 * M31 — Failover Management Screen (§13 / §31). Live register of failover events
 * by type with the affected service, source/target environments, status and
 * measured downtime (§30 SoD: emergency/manual failovers cannot be self-approved).
 * Supports initiating a failover, marking it in progress, completing it with the
 * measured downtime and recording a failure — each wired to the guarded
 * /v1/bcm/failovers write API. Filterable by failover type and status.
 */
export function FailoverManagementPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<FailoverRow[]>([]);
  const [failoverType, setFailoverType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newService, setNewService] = useState('');
  const [newType, setNewType] = useState('MANUAL');
  const [downtime, setDowntime] = useState<Record<string, string>>({});
  const [failDetail, setFailDetail] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (failoverType) params.set('failoverType', failoverType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: FailoverRow[] }>(`/v1/bcm/failovers${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, failoverType, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m31.failover.err403')
      : (err as Error).message;

  const initiate = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newService.trim()) { setActionError(tr('m31.failover.errService')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/bcm/failovers`, {
        method: 'POST',
        body: { affectedService: newService.trim(), failoverType: newType },
        token,
      });
      setNotice(tr('m31.failover.noticeInitiated', { service: newService.trim() }));
      setNewService('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: FailoverRow, path: string, label: string, body?: unknown): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/bcm/failovers/${r.failoverId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(tr('m31.failover.actNotice', { code: r.failoverCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  const complete = async (r: FailoverRow): Promise<void> => {
    const raw = (downtime[r.failoverId] ?? '').trim();
    const body = raw ? { downtimeSeconds: Number(raw) } : {};
    await act(r, 'complete', tr('m31.failover.completed'), body);
  };

  const fail = async (r: FailoverRow): Promise<void> => {
    const detail = (failDetail[r.failoverId] ?? '').trim();
    if (!detail) { setActionError(tr('m31.failover.errFail')); return; }
    await act(r, 'fail', tr('m31.failover.markedFailed'), { errorDetail: detail });
  };

  return (
    <main aria-label="failover-management">
      <h1>{tr('m31.failover.title')}</h1>

      <section aria-label="initiate-failover">
        <h2>{tr('m31.failover.initiateHeading')}</h2>
        <form aria-label="failover-form" onSubmit={(e) => void initiate(e)}>
          <label>{tr('m31.failover.fieldService')}<input aria-label="field-affected-service" value={newService} onChange={(e) => setNewService(e.target.value)} /></label>
          <label>{tr('m31.failover.fieldType')}
            <select aria-label="field-failover-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {INITIATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-failover" disabled={busy}>{busy ? tr('m31.failover.busyInitiate') : tr('m31.failover.submitInitiate')}</button>
        </form>
        {actionError && <p role="alert" aria-label="failover-error">{actionError}</p>}
        {notice && <p aria-label="failover-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m31.failover.filterType')}
          <select aria-label="filter-failover-type" value={failoverType} onChange={(e) => setFailoverType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m31.failover.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m31.failover.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m31.failover.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m31.failover.loading')}</p> : (
        <>
          <p aria-label="failover-count">{tr('m31.failover.count', { count: rows.length })}</p>
          <table aria-label="failover-table">
            <thead><tr><th>{tr('m31.failover.thCode')}</th><th>{tr('m31.failover.thService')}</th><th>{tr('m31.failover.thType')}</th><th>{tr('m31.failover.thSource')}</th><th>{tr('m31.failover.thTarget')}</th><th>{tr('m31.failover.thStatus')}</th><th>{tr('m31.failover.thDowntime')}</th><th>{tr('m31.failover.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.failoverId} data-status={r.status}>
                  <td>{r.failoverCode}</td><td>{r.affectedService}</td><td>{r.failoverType}</td>
                  <td>{r.sourceEnvironment ?? '—'}</td><td>{r.targetEnvironment ?? '—'}</td>
                  <td>{r.status}</td>
                  <td>{r.downtimeSeconds === null ? '—' : `${r.downtimeSeconds}s`}</td>
                  <td>
                    {r.status === 'INITIATED' && (
                      <button type="button" aria-label={`start-${r.failoverCode}`} onClick={() => void act(r, 'start', tr('m31.failover.inProgress'))}>{tr('m31.failover.start')}</button>
                    )}
                    {(r.status === 'INITIATED' || r.status === 'IN_PROGRESS') && (
                      <>
                        <input aria-label={`downtime-input-${r.failoverCode}`} type="number" placeholder={tr('m31.failover.downtimePlaceholder')} value={downtime[r.failoverId] ?? ''} onChange={(e) => setDowntime((m) => ({ ...m, [r.failoverId]: e.target.value }))} />
                        <button type="button" aria-label={`complete-${r.failoverCode}`} onClick={() => void complete(r)}>{tr('m31.failover.complete')}</button>
                        <input aria-label={`fail-input-${r.failoverCode}`} placeholder={tr('m31.failover.failPlaceholder')} value={failDetail[r.failoverId] ?? ''} onChange={(e) => setFailDetail((m) => ({ ...m, [r.failoverId]: e.target.value }))} />
                        <button type="button" aria-label={`fail-${r.failoverCode}`} onClick={() => void fail(r)}>{tr('m31.failover.markFailed')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
